import React, { useContext, useMemo, useState } from 'react';
import classes from './SuggestionChips.module.css';
import { auth } from '../../config';
import { UserContext } from '../../store/user-context';
import { addGearItems, getItemCategories } from '../../config/helpers';

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const dismissKey = (category) => `suggestions_dismissed_${category}`;

// Optional (default: false) defaultitems for one category, shown as
// tappable "+ Name" chips under that category's gear list. `optionalItems`
// is the whole default:false catalog, fetched once by the parent — this
// just filters it down to the current category and to names not already
// in the group.
const SuggestionChips = ({ group, category, optionalItems }) => {
  const UserCtx = useContext(UserContext);
  const user = auth.currentUser;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissKey(category)) === 'true');
  const [addingName, setAddingName] = useState(null);
  // Hides a chip the instant its add is kicked off, rather than waiting on
  // the group's live Firestore listener to reflect the write.
  const [justAdded, setJustAdded] = useState([]);

  const suggestions = useMemo(() => {
    if (!category) return [];
    const existingNames = (group?.items || []).map(item => item.name?.toLowerCase());
    return (optionalItems || []).filter(item =>
      getItemCategories(item).includes(category)
      && !existingNames.includes(item.name?.toLowerCase())
      && !justAdded.includes(item.name)
    );
  }, [optionalItems, category, group?.items, justAdded]);

  const handleAdd = async (item) => {
    if (addingName) return;
    setAddingName(item.name);
    setJustAdded(prev => [...prev, item.name]);
    try {
      const festival = UserCtx?.festival || group?.festival;
      const defaultItemsList = UserCtx?.itemInfo;
      const itemInfoList = festival?.items || defaultItemsList || [];
      // Suggestion chips sit at the bottom of the category's list, so the
      // added item should land there too — prepending (the default) would
      // add it off-screen above everything, with no visible feedback that
      // tapping the chip did anything.
      await addGearItems(group, [item.name], category, itemInfoList, user?.uid, 'bottom');
    } catch (err) {
      console.log('Error adding suggested item:', err);
      setJustAdded(prev => prev.filter(n => n !== item.name));
    }
    setAddingName(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(dismissKey(category), 'true');
    setDismissed(true);
  };

  const handleShowSuggestions = () => {
    localStorage.removeItem(dismissKey(category));
    setDismissed(false);
  };

  if (dismissed) {
    return (
      <button className={classes.showLink} onClick={handleShowSuggestions}>
        Show suggestions
      </button>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <div className={classes.row}>
      <div className={classes.scroll}>
        {suggestions.map(item => (
          <button
            key={item.id || item.name}
            className={classes.chip}
            onClick={() => handleAdd(item)}
            disabled={addingName === item.name}
          >
            <span className={classes.plus}>+ </span>{item.name}
          </button>
        ))}
      </div>
      <button className={classes.dismissBtn} onClick={handleDismiss} aria-label="Dismiss suggestions">
        <CloseIcon />
      </button>
    </div>
  );
};

export default SuggestionChips;
